// src/types/auth.ts

// =============================================
// Auth Related Types
// =============================================

export const LoginType = {
  EMAIL: "email",
  PHONE: "phone",
} as const;

export type LoginType = (typeof LoginType)[keyof typeof LoginType];

export const UserRole = {
  CUSTOMER: "CUSTOMER",
  ADMIN: "ADMIN",
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

// Login DTO
export interface LoginDto {
  email?: string;
  phone?: string;
  password: string;
  type: LoginType;
}

// Register DTO
export interface RegisterDto {
  name: string;
  email: string;
  phone?: string;
  password: string;
}

// Change Password DTO
export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

// Forgot Password DTO
export interface ForgotPasswordDto {
  email: string;
}

// Reset Password DTO
export interface ResetPasswordDto {
  email: string;
  otp: string;
  password: string;
  passwordConfirmation: string;
}

// Verify Email DTO
export interface VerifyEmailDto {
  email: string;
  otp: string;
}

// Refresh Token DTO
export interface RefreshTokenDto {
  refreshToken: string;
}

// Auth User
export interface AuthUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  avatar?: string;
  emailVerifiedAt?: Date | null;
  isVerified: boolean;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Auth Tokens
export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

// JWT Payload
export interface JwtPayload {
  sub: number; // User ID
  email: string;
  phone?: string;
  role: UserRole;
  iat?: number;
  exp?: number;
}

// Auth Response
export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: Omit<AuthUser, "password">;
    tokens?: AuthTokens; // Dashboard style with refresh tokens
    token?: string; // Mobile style simple token
    requiresEmailVerification?: boolean;
  };
}

// Mobile Auth Response (simplified)
export interface MobileAuthResponse {
  user: Omit<AuthUser, "password">;
  token: string;
  requiresEmailVerification?: boolean;
}

// Dashboard Auth Response (detailed)
export interface DashboardAuthResponse {
  user: Omit<AuthUser, "password">;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

// Login Response
export interface LoginResponse extends AuthResponse {}

// Refresh Response
export interface RefreshResponse {
  success: boolean;
  message?: string;
  data: {
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
  };
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}
